/**
 * « Réduire les textes trop larges » : un calcul ou un texte qui sort de la
 * scène (coupé sur le téléphone) passe à la taille qui le fait tenir. La
 * création par l'IA le fait d'office depuis le 21/10 ; le lecteur réduit
 * aussi à l'affichage. Ce bouton corrige un brouillon plus ancien.
 */

import { useMemo, useState } from 'react';
import { Minimize2 } from 'lucide-react';
import { fitTextsToCanvas } from '@jeraisonne/animation';
import { checkedDraft, useSaveAnimationDraft, type AnimationRow } from '@/lib/animations/api';

export function AnimationFitTexts({
  row,
  toolIds,
}: {
  row: Pick<AnimationRow, 'id' | 'draft'>;
  toolIds?: readonly string[];
}) {
  const fit = useMemo(() => fitTextsToCanvas(row.draft), [row.draft]);
  const save = useSaveAnimationDraft();
  const [done, setDone] = useState<number | null>(null);

  if (fit.fits.length === 0)
    return done ? (
      <p role="status" className="text-success-600 mb-2 text-[13px] font-semibold">
        {done > 1
          ? `${done} textes réduits : ils tiennent dans la scène.`
          : 'Texte réduit : il tient dans la scène.'}
      </p>
    ) : null;
  const n = fit.fits.length;
  return (
    <div className="border-warning-500/40 bg-warning-50 mb-2 rounded-xl border p-3">
      <p className="text-ink-700 text-[13px]">
        {n > 1 ? `${n} textes sortent de la scène` : 'Un texte sort de la scène'} (coupés sur un
        téléphone) :{' '}
        {fit.fits.map((f, i) => (
          <span key={f.objectId}>
            {i > 0 && ', '}
            <code className="text-[12px]">{f.objectId}</code> {f.from} → {f.to} px
          </span>
        ))}
        .
      </p>
      <button
        type="button"
        disabled={save.isPending}
        onClick={() =>
          save.mutate(
            { id: row.id, ...checkedDraft(fit.doc, toolIds) },
            { onSuccess: () => setDone(n) },
          )
        }
        className="bg-primary-500 hover:bg-primary-600 mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white disabled:opacity-60"
      >
        <Minimize2 className="h-4 w-4" aria-hidden />
        {save.isPending
          ? 'Enregistrement…'
          : n > 1
            ? `Réduire les ${n} textes`
            : 'Réduire le texte'}
      </button>
      {save.error && (
        <p role="alert" className="text-error-600 mt-2 text-[13px]">
          {save.error.message}
        </p>
      )}
    </div>
  );
}
